import {
  Box,
  Button,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { EventDetailTicketTypesSection } from "@common/interfaces/KYDEvent";
import { FaCloudDownloadAlt, FaPlus } from "react-icons/fa";
import { SectionProps } from "../sections/sectionsComponents";
import { EditModalType, TieredTicketTypes } from "../../../utils";
import { useCallback, useMemo, useState } from "react";
import { NoVenueBanner } from "../Venue/NoVenueBanner";
import {
  AdminTicketType,
  CreateTicketTypeRequest,
  KYDTicketType,
} from "@common/interfaces/KYDTicket";
import EditTicketTypeModal from "./EditTicketTypeModal";
import {
  PriceMode,
  TicketTypeTier,
  TicketTypeValues,
  getTiers,
} from "./TieredTicketType/schema";
import { useDialog, useDialogWithOptions } from "../../../Common/Dialog";
import actions from "../../../actions";
import { useLoading } from "../../hooks/form";
import { TicketTypesTable } from "./TicketTypesTable";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import DeleteTicketTypeDialog, {
  DeleteTicketTypeDialogContext,
  withDeleteTicketTypeDialog,
} from "./DeleteTicketTypeDialog";
import DeleteTierDialog, {
  DeleteTierDialogContext,
  withDeleteTierDialog,
} from "./DeleteTierDialog";
import EventDetailTitle from "../../EventDetailTitle";

const TicketTypesSection = ({
  data,
  modalType,
  setModalType,
  permissions,
  isLoading,
  refetchEvent,
}: SectionProps<EventDetailTicketTypesSection["data"]>) => {
  const [editingTicketType, setEditingTicketType] =
    useState<AdminTicketType | null>(null);
  const [deletingTier, setDeletingTier] = useState<TicketTypeTier | null>(null);
  const toast = useToast();
  const dialog = useDialog();
  const deleteDialog = useDialogWithOptions(DeleteTicketTypeDialogContext);
  const deleteTierDialog = useDialogWithOptions(DeleteTierDialogContext);
  const [loading, updateLoading] = useLoading({
    tickettype: false,
  });

  const tiers: TieredTicketTypes | {} = useMemo(() => {
    if (data.ticket_types) {
      const _tiers: TieredTicketTypes = {};

      const findNext = (next_tier_id: string) => {
        const next_tier = data.tiered_ticket_types.find(
          (tt) => tt.id === next_tier_id
        );
        return next_tier;
      };

      for (const ticket_type of data.ticket_types) {
        if (ticket_type.tier === 0) {
          _tiers[ticket_type.id] = [];
          let next_tier_id: string | undefined = ticket_type.next_tier_id;
          if (next_tier_id) {
            do {
              const next_tier = findNext(next_tier_id);
              if (next_tier) {
                _tiers[ticket_type.id].push(next_tier);
                next_tier_id = next_tier?.next_tier_id;
              } else {
                next_tier_id = undefined;
              }
            } while (next_tier_id);
          }
        }
      }

      return _tiers;
    }
  }, [data]);

  const openEditModal = (ticket_type: AdminTicketType) => {
    if (ticket_type.pool) {
      setModalType(EditModalType.EDIT_TIERED_TICKET_TYPE);
    } else {
      setModalType(EditModalType.TICKET_TYPE);
    }

    setEditingTicketType(ticket_type);
  };

  const onUpdateTicketType = useCallback(
    async (_data: TicketTypeValues) => {
      updateLoading("tickettype", true);
      const { id, ...editData } = _data;

      const request = {
        on_sale_at: editData.on_sale_at,
        off_sale_at: editData.off_sale_at,
        start_at: editData.start_at,
        end_at: editData.end_at,
        code: editData.code,
        hidden: editData.code ? true : editData.hidden,
        description: editData.description,
        name: editData.name,
        limit: editData.limit,
        quantity: editData.quantity,
        price: editData.free ? "0" : editData.price,
        waitlist_status: editData.waitlist_status,
        waitlist_return: editData.waitlist_return,
        increment: editData.increment,
      };

      try {
        const ticketType = data?.ticket_types.find((tt) => tt.id === id);
        if (!ticketType) {
          throw Error("No ticket type found");
        }

        const result = await actions.ADMIN.updateTicketType(
          data.id,
          id,
          {
            ...request,
            on_sale_at: request.on_sale_at?.toString(),
            off_sale_at: request.off_sale_at?.toString(),
            start_at: request.start_at?.toString(),
            end_at: request.end_at?.toString(),
          },
          {
            last_ledger_entry_index: ticketType.last_ledger_entry_index ?? 0,
          }
        );

        await refetchEvent({ silent: true });
        toast({
          title: "Updated",
          description: "Ticket type has been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("tickettype", false);

      setEditingTicketType(null);
      setModalType(EditModalType.INACTIVE);
    },
    [
      data,
      setModalType,
      setEditingTicketType,
      updateLoading,
      refetchEvent,
      toast,
      dialog,
    ]
  );

  const onCreateTicketType = useCallback(
    async (_data: TicketTypeValues) => {
      updateLoading("tickettype", true);

      try {
        const request: CreateTicketTypeRequest = {
          type: "GA",
          mode: "website",
          name: _data.name,
          quantity: _data.quantity?.toString(),
          code: _data.code ?? undefined,
          price: _data.free ? "0" : _data.price,
          shift_quantity_on_expiration: _data.shift_quantity_on_expiration,
          limit: _data.limit?.toString(),
          description: _data.description,
          on_sale_at: _data.on_sale_at?.toString(),
          off_sale_at: _data.off_sale_at?.toString(),
          free: _data.free,
          start_at: _data.start_at?.toString(),
          end_at: _data.end_at?.toString(),
          waitlist_status:
            _data.waitlist_status as KYDTicketType["waitlist_status"],
          waitlist_return:
            _data.waitlist_return as KYDTicketType["waitlist_return"],
          hidden: _data.hidden,
          increment: _data.increment,
        };

        if (_data.price_mode === PriceMode.TIERED_ALLOCATION) {
          request.tiers = _data.tiers;
        }

        await actions.ADMIN.createTicketType(data.id, request);

        await refetchEvent({ silent: true });
        setEditingTicketType(null);
        setModalType(EditModalType.INACTIVE);

        toast({
          title: "✅ Ticket Type Created",
          description: "Ticket type has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        console.error(err);
        dialog({ text: err.message });
      }

      updateLoading("tickettype", false);
    },
    [data, refetchEvent, dialog]
  );

  const onCloseEditTieredTicketTypeModal = () => {
    setEditingTicketType(null);
    setModalType(EditModalType.INACTIVE);
  };

  const onUpdateTieredTicketType = useCallback(
    async (updateData: TicketTypeValues) => {
      // TODO: fix loading
      updateLoading("tickettype", true);

      const mainTicketType = data?.ticket_types.find(
        (tt) => tt.id === updateData.id
      );
      try {
        if (!mainTicketType) {
          throw Error("No ticket type found");
        }

        const on_sale_at = updateData.on_sale_at?.toString();
        const off_sale_at = updateData.off_sale_at?.toString();
        const start_at = updateData.start_at?.toString();
        const end_at = updateData.end_at?.toString();
        const code = updateData.code;
        const description = updateData.description;
        const shift_quantity_on_expiration =
          updateData.shift_quantity_on_expiration;
        // const limit = data.limit.toString(); // doesn't exist on server
        const tt_tiers = updateData.tiers || [];
        const waitlist_status = updateData.waitlist_status;
        const waitlist_return = updateData.waitlist_return;
        const increment = updateData.increment;
        const limit = updateData.limit;
        const commonFields = {
          on_sale_at,
          off_sale_at,
          start_at,
          end_at,
          code,
          description,
          waitlist_status,
          waitlist_return,
          shift_quantity_on_expiration,
          increment,
          limit,
        };
        const ticketTypeUpdatesById = new Map<string, any>();

        const changedAnythingOutsideTier =
          on_sale_at ||
          off_sale_at ||
          start_at ||
          end_at ||
          code ||
          description ||
          waitlist_status ||
          waitlist_return ||
          increment ||
          shift_quantity_on_expiration ||
          limit;

        if (changedAnythingOutsideTier) {
          const allTicketTypeIdsOfTiers = getTiers(mainTicketType, {
            tiered_ticket_types: data.tiered_ticket_types,
            timezone: data.timezone,
          });
          // If the tiers have updates already, they'll be added to the ticket types anyway
          // Thus we need to remove them

          // When updating the "outside" ticket type all the ticket types need tobe there
          // So that all the updates are pushed to the tiered ticket types
          allTicketTypeIdsOfTiers.forEach((tt) => {
            ticketTypeUpdatesById.set(tt.id, {
              id: tt.id,
              ...commonFields,
            });
          });
        }

        for (const tier of tt_tiers) {
          if (!tier) {
            continue;
          }

          if (Object.keys(tier).length > 0) {
            const ticket_type_update: any = {
              id: tier?.id,
              ...(ticketTypeUpdatesById.get(tier.id) || {}),
            };

            Object.keys(tier).forEach((k) => {
              const key = k;
              const value = tier[k];

              if (key === "quantity" && parseInt(value) > tier.quantity) {
                console.log("Invalid quantity update");
              } else {
                ticket_type_update[key] = value;
              }
            });
            ticketTypeUpdatesById.set(tier.id, ticket_type_update);
          }
        }

        await actions.ADMIN.updateTicketTypes(
          data.id!,
          Array.from(ticketTypeUpdatesById.values()),
          {
            last_ledger_entry_index: mainTicketType.last_ledger_entry_index,
          }
        );
        await refetchEvent({ silent: true });
        toast({
          title: "Updated",
          description: "Ticket type has been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        console.error(err);
        dialog({ text: err.message });
      }

      updateLoading("tickettype", false);

      setEditingTicketType(null);
      setModalType(EditModalType.INACTIVE);
    },
    [data, refetchEvent, updateLoading, dialog, toast]
  );

  const openDeleteDialog = async (ticketTypeId: string) => {
    deleteDialog.open();
  };

  const onRemoveTier = useCallback(
    async ({ id }: { id: string }) => {
      try {
        updateLoading("tickettype", true);
        await actions.ADMIN.deleteTicketType(data.id, id);
        await refetchEvent({ silent: true }).then((event) => {
          setEditingTicketType(event.ticket_types.find((tt) => tt.id === id));
        });
      } catch (e) {
        console.error(e);
        dialog({ text: e.message });
      } finally {
        setDeletingTier(null);
        deleteTierDialog.close();

        updateLoading("tickettype", false);
      }
    },
    [data, refetchEvent, dialog, updateLoading]
  );

  const onDeleteTicketType = useCallback(
    async (ticketTypeId: string) => {
      if (!ticketTypeId) {
        console.error("No ticket type id provided");

        return;
      }

      const ticketType = data?.ticket_types.find(
        (tt) => tt.id === ticketTypeId
      );

      try {
        updateLoading("tickettype", true);
        await actions.ADMIN.deleteTicketType(data.id, ticketTypeId);
        await refetchEvent({ silent: true });
        setEditingTicketType(null);
        setModalType(EditModalType.INACTIVE);

        toast({
          title: `${ticketType?.name} deleted`,
          description: "Ticket type with no sales has been deleted.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }

      deleteDialog.close();
      updateLoading("tickettype", false);
    },
    [data, refetchEvent, deleteDialog, dialog, updateLoading]
  );

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  const isEditingRegularTicketType = useMemo(
    () =>
      modalType === EditModalType.TICKET_TYPE ||
      (editingTicketType && !("tier" in editingTicketType)),
    [modalType, editingTicketType]
  );

  const isEditingTieredTicketType = useMemo(
    () =>
      modalType === EditModalType.EDIT_TIERED_TICKET_TYPE &&
      editingTicketType &&
      "tier" in editingTicketType,
    [modalType, editingTicketType]
  );

  const isTicketTypeQuantityUpdating = useMemo(() => {
    const all_ticket_types = [
      ...(data?.ticket_types || []),
      ...(data?.tiered_ticket_types || []),
    ];
    return all_ticket_types.some(
      (ticket_type) =>
        ticket_type.status !== "DRAFT" &&
        (ticket_type.ledger_processing_status === "PROCESSING" ||
          (ticket_type.pending_ledger_entries || 0) > 0)
    );
  }, [data?.ticket_types, data?.tiered_ticket_types]);

  const hasTicketTypeQuantityUpdateFailure = useMemo(() => {
    const all_ticket_types = [
      ...(data?.ticket_types || []),
      ...(data?.tiered_ticket_types || []),
    ];
    return all_ticket_types.some(
      (ticket_type) => ticket_type.ledger_processing_status === "FAILED"
    );
  }, [data?.ticket_types, data?.tiered_ticket_types]);

  return (
    <Stack spacing={2}>
      <DeleteTicketTypeDialog
        onDelete={(id: string) => onDeleteTicketType(id)}
        onClose={() => deleteDialog.close()}
        ticketType={editingTicketType}
        isLoading={loading.tickettype}
      />
      <DeleteTierDialog
        onDelete={(id: string) => onRemoveTier({ id })}
        onClose={() => deleteTierDialog.close()}
        ticketType={deletingTier}
        isLoading={loading.tickettype}
      />
      {isEditingRegularTicketType && (
        <EditTicketTypeModal
          onSubmit={editingTicketType ? onUpdateTicketType : onCreateTicketType}
          mode={editingTicketType ? "edit" : "create"}
          onDelete={openDeleteDialog}
          isLoading={loading.tickettype}
          currency={data.currency}
          data={data}
          link={data.short_link || data.link}
          timezone={data.timezone}
          onClose={() => onCloseEditTieredTicketTypeModal()}
          ticketType={editingTicketType}
          onRemoveTier={async (tier: TicketTypeTier) => {
            setDeletingTier(tier);
            deleteTierDialog.open();
          }}
        />
      )}
      {isEditingTieredTicketType && (
        <EditTicketTypeModal
          mode={editingTicketType ? "edit" : "create"}
          isLoading={loading.tickettype}
          onSubmit={
            editingTicketType ? onUpdateTieredTicketType : onCreateTicketType
          }
          onDelete={openDeleteDialog}
          currency={data.currency}
          data={data}
          link={data.short_link || data.link}
          timezone={data.timezone}
          onClose={() => onCloseEditTieredTicketTypeModal()}
          ticketType={editingTicketType}
          onRemoveTier={async (tier: TicketTypeTier) => {
            setDeletingTier(tier);
            deleteTierDialog.open();
          }}
        />
      )}
      <Stack
        direction={{ base: "column", md: "row" }}
        w="100%"
        alignItems={"center"}
      >
        <HStack w="100%" justify={"space-between"}>
          <HStack spacing={2}>
            <EventDetailTitle>Ticket Types</EventDetailTitle>
            {isTicketTypeQuantityUpdating ? (
              <Spinner size="sm" color="gray.500" />
            ) : null}
          </HStack>

          <ShowIfHasPermission permissions={permissions} checkFor="write">
            <IconButton
              isDisabled={isLoading}
              variant={"outline"}
              aria-label="Add ticket type"
              icon={<FaPlus />}
              onClick={() => {
                setModalType(EditModalType.TICKET_TYPE);
              }}
            />
          </ShowIfHasPermission>
        </HStack>
      </Stack>
      {hasTicketTypeQuantityUpdateFailure ? (
        <Text color="red.500" fontSize="sm">
          One or more ticket type quantity updates have failed. Support has been
          notified. You will not be able to make any quantity updates until this
          is resolved by KYD
        </Text>
      ) : null}
      <Box
        overflowX={(data?.ticket_types || []).length === 0 ? "inherit" : "auto"}
      >
        <TicketTypesTable
          realtimeMetrics={data?.realtime_metrics}
          readOnly={!hasPermission("write")}
          eventId={data.id}
          onEdit={openEditModal}
          isDisabled={!data.hasTimelinesSet || isLoading}
          ticketTypes={data?.ticket_types}
          tiers={tiers}
          isLoading={isLoading}
        />
        <NoVenueBanner show={!data.hasTimelinesSet} type="tickettype" />
      </Box>
    </Stack>
  );
};

export default withDeleteTicketTypeDialog(
  withDeleteTierDialog(TicketTypesSection)
);
